// "Historial" en la esquina del Asistente de Crear (ADR-2026-10-13-historial-en-el-asistente):
// en lugar de "Ver creaciones", un botón que despliega las últimas conversaciones y abre la
// elegida. La lista se pide al abrir (no antes), de la más reciente a la más vieja. En el
// Asistente (y en la portada de `/agente`) es el MISMO historial del Agente, `listChats`
// (ADR-2026-10-18-un-solo-historial-con-lo-de-principal); en Imagen y Video, las sesiones de
// Crear (`listSesionesRedes`).

import React, { useEffect, useRef, useState } from 'react';
import { Check, History, Loader2 } from 'lucide-react';
import { fechaDelChat, listChats, listSesionesRedes, type SesionDeRedesResumen } from '../../agentChatApi';

/** Cuántas conversaciones entran en el desplegable (las sesiones de Crear ya vienen topadas). */
const TOPE_DE_CONVERSACIONES = 30;

/** El historial compartido con la forma de la lista, fechado por el último mensaje. */
const conversaciones = async (): Promise<SesionDeRedesResumen[]> =>
  (await listChats())
    .slice(0, TOPE_DE_CONVERSACIONES)
    .map((c) => ({ id: c.id, titulo: c.title || 'Conversación', actualizadoEn: fechaDelChat(c) }));

/** "hoy 14:05", "ayer", "12 oct". */
function cuando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hoy = new Date();
  const mismoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (mismoDia(d, hoy)) return `hoy ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`;
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  if (mismoDia(d, ayer)) return 'ayer';
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

export const HistorialDeSesiones: React.FC<{
  /** La sesión abierta: se marca en la lista. */
  actual?: string;
  onElegir: (id: string) => void;
  /** Sólo conversaciones: el historial compartido con el Agente (`listChats`), con sus chats y
   *  las sesiones de Crear donde se charló. Las de la caja (Imagen y Video) no tienen mensajes:
   *  en el Historial del Asistente abrían una grilla de creaciones en vez de una conversación
   *  (ADR-2026-10-17-el-historial-del-asistente-abre-conversaciones). */
  soloConversaciones?: boolean;
  /** Las clases del botón (la pastilla de la esquina de Crear). */
  claseDelBoton: string;
  className?: string;
}> = ({ actual, onElegir, soloConversaciones = false, claseDelBoton, className = '' }) => {
  const [abierto, setAbierto] = useState(false);
  const [sesiones, setSesiones] = useState<SesionDeRedesResumen[] | null>(null);
  const raizRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    setSesiones(null);
    void (soloConversaciones ? conversaciones() : listSesionesRedes()).then((s) => {
      if (vivo) setSesiones(s);
    });
    const alTocarAfuera = (e: MouseEvent) => {
      if (raizRef.current && !raizRef.current.contains(e.target as Node)) setAbierto(false);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', alTocarAfuera);
    document.addEventListener('keydown', alTeclear);
    return () => {
      vivo = false;
      document.removeEventListener('mousedown', alTocarAfuera);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto, soloConversaciones]);

  return (
    <div ref={raizRef} className={className}>
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        aria-haspopup="menu"
        className={claseDelBoton}
      >
        <History className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        Historial
      </button>
      {abierto && (
        <div
          role="menu"
          aria-label="Tus últimas conversaciones"
          className="absolute right-0 top-12 z-30 w-[300px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-white p-1.5 shadow-[0_18px_50px_-18px_rgb(var(--brand-black-rgb)_/_0.35)] ring-1 ring-black/[0.06] animate-fade-in"
        >
          <p className="px-2.5 pb-1 pt-1.5 text-[12px] font-semibold text-[rgb(var(--brand-black-rgb)_/_0.45)]">Tus últimas conversaciones</p>
          <div className="max-h-[360px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {sesiones === null ? (
              <div className="flex items-center gap-2 px-2.5 py-3 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Cargando…
              </div>
            ) : sesiones.length === 0 ? (
              <p className="px-2.5 py-3 text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">Todavía no hay conversaciones.</p>
            ) : (
              sesiones.map((s) => {
                const esLaActual = s.id === actual;
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setAbierto(false);
                      if (!esLaActual) onElegir(s.id);
                    }}
                    className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left transition-colors ${
                      esLaActual ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.06)]' : 'hover:bg-[#F3F4F8]'
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-[13.5px] font-medium ${esLaActual ? 'text-brand-primary' : 'text-brand-black'}`}>
                        {s.titulo}
                      </span>
                      <span className="block text-[11.5px] text-[rgb(var(--brand-black-rgb)_/_0.45)]">{cuando(s.actualizadoEn)}</span>
                    </span>
                    {esLaActual && <Check className="h-4 w-4 shrink-0 text-brand-primary" aria-hidden="true" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HistorialDeSesiones;
